import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Source {
  state: string;
  default_url: string;
  size_mb: number;
  note: string;
  loaded: boolean;
  ingested_at: string | null;
  row_counts: { mupolygon?: number; muaggatt?: number; component?: number } | null;
  last_job: {
    id: number;
    status: string;
    progress_pct: number;
    log_tail: string | null;
    error: string | null;
    started_at: string;
    completed_at: string | null;
  } | null;
}

const fmtNum = (n: number) => Math.round(n).toLocaleString();
const fmtTime = (iso: string | null) =>
  !iso ? '—' : new Date(iso).toLocaleString();

export default function GeoDataPage() {
  const qc = useQueryClient();
  const [urls, setUrls] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery<{ sources: Source[] }>({
    queryKey: ['geohub-data-sources'],
    queryFn: () => api.get('/geohub-data/sources'),
    refetchInterval: (q) => {
      // Poll every 4s while any job is in flight
      const sources = (q.state.data as any)?.sources ?? [];
      const running = sources.some((s: Source) => s.last_job && ['pending','downloading','extracting','loading'].includes(s.last_job.status));
      return running ? 4000 : false;
    },
  });

  const trigger = useMutation({
    mutationFn: ({ state, url }: { state: string; url: string }) =>
      api.post('/geohub-data/ingest', { state, source_url: url }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geohub-data-sources'] }),
  });

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ margin: 0, fontSize: 22 }}>Geo Data</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 4, maxWidth: 720 }}>
        Reference data layers loaded into GeoHub. SSURGO soil data drives the
        ground-method classifier (plow / trench / rock saw / HDD / sawcut),
        which sets per-segment build cost from real ground conditions instead
        of a flat per-foot rate.
      </p>

      <h2 style={{ fontSize: 14, marginTop: 28, color: '#334155', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
        USDA SSURGO — Soil Survey Geographic Database
      </h2>
      <p style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>
        Source: <a href="https://nrcs.app.box.com/v/soils" target="_blank" rel="noreferrer">NRCS Box (gSSURGO state mosaics)</a>.
        Find the latest gSSURGO_<i>STATE</i>.zip URL there and paste below.
      </p>

      {isLoading && <div style={{ color: '#64748b' }}>Loading…</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 12, marginTop: 12 }}>
        {(data?.sources ?? []).map((s) => {
          const job = s.last_job;
          const running = job && ['pending','downloading','extracting','loading'].includes(job.status);
          const failed = job && job.status === 'failed';
          const url = urls[s.state] ?? s.default_url ?? '';
          return (
            <div key={s.state} style={{
              border: '1px solid ' + (failed ? '#fca5a5' : running ? '#fbbf24' : s.loaded ? '#86efac' : '#cbd5e1'),
              borderRadius: 8,
              padding: 14,
              background: '#fff',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>{s.state}</h3>
                <span style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 700,
                  background: failed ? '#fee2e2' : running ? '#fef3c7' : s.loaded ? '#dcfce7' : '#f1f5f9',
                  color:      failed ? '#b91c1c' : running ? '#92400e' : s.loaded ? '#047857' : '#64748b',
                }}>
                  {failed ? 'FAILED' : running ? job!.status.toUpperCase() : s.loaded ? 'LOADED' : 'NOT LOADED'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{s.note}</div>

              {s.loaded && s.row_counts && (
                <div style={{ marginTop: 10, fontSize: 11, background: '#f8fafc', padding: 8, borderRadius: 4, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px' }}>
                  <span style={{ color: '#64748b' }}>Loaded:</span><span>{fmtTime(s.ingested_at)}</span>
                  <span style={{ color: '#64748b' }}>Map units:</span><span>{fmtNum(Number(s.row_counts.mupolygon ?? 0))}</span>
                  <span style={{ color: '#64748b' }}>Agg attrs:</span><span>{fmtNum(Number(s.row_counts.muaggatt ?? 0))}</span>
                  <span style={{ color: '#64748b' }}>Components:</span><span>{fmtNum(Number(s.row_counts.component ?? 0))}</span>
                </div>
              )}

              {running && job && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, color: '#92400e', marginBottom: 3 }}>{job.status} · {job.progress_pct}%</div>
                  <div style={{ height: 6, background: '#fef3c7', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${job.progress_pct}%`, background: '#f59e0b', transition: 'width 0.3s' }} />
                  </div>
                  {job.log_tail && (
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: 60, overflow: 'auto' }}>{job.log_tail}</div>
                  )}
                </div>
              )}

              {failed && job && (
                <div style={{ marginTop: 10, fontSize: 11, color: '#b91c1c', background: '#fee2e2', padding: 8, borderRadius: 4, fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto' }}>
                  {job.error ?? 'failed (no error message)'}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Source URL (gSSURGO_{s.state}.zip)</label>
                <input
                  type="url"
                  placeholder="https://nrcs.box.com/shared/static/...zip"
                  value={url}
                  onChange={(e) => setUrls({ ...urls, [s.state]: e.target.value })}
                  disabled={!!running}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 11, fontFamily: 'monospace', border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>~{s.size_mb} MB compressed</span>
                  <button
                    disabled={!!running || !url}
                    onClick={() => trigger.mutate({ state: s.state, url })}
                    style={{
                      padding: '6px 14px', fontSize: 12, fontWeight: 600,
                      background: running ? '#cbd5e1' : '#0f172a',
                      color: '#fff', border: 0, borderRadius: 4,
                      cursor: running ? 'not-allowed' : (url ? 'pointer' : 'not-allowed'),
                      opacity: !url ? 0.5 : 1,
                    }}
                  >
                    {s.loaded ? '↻ Re-download' : '⤓ Download'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 24, fontSize: 11, color: '#94a3b8', maxWidth: 720 }}>
        After loading, the ground-method classifier becomes available in the auto-design pipeline.
        Each segment will be tagged plow / trench / rock saw / HDD / sawcut based on the
        SSURGO map-unit it crosses, and the BoM will reflect method-specific install rates.
      </div>
    </div>
  );
}
